import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Form, Modal, Table } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import pharmacyService from '../../../services/pharmacyService';
import { usePharmacy } from '../pharmacyContext';
import { PHARMACY_COPY, statusLabel } from '../pharmacyCopy';
import Icon from '../../common/Icons';
import {
  PharmacyPageHeader,
  LoadingBlock,
  EmptyBlock,
  formatDate,
  formatDateTime,
  formatNumber
} from '../ui';
import {
  InventoryFlow,
  InventoryProgress,
  focusAdjacentCount,
  inventoryStatusClass,
  isLineDirty,
  lineVariance,
  mergeKeptDrafts,
  parseCountedDraft,
  seedCountDrafts,
  useInventoryRoutes
} from '../components/inventoryUi';
import { inventorySessionDisplayName } from '../components/inventorySites';
import MedicationSmartPicker from '../components/MedicationSmartPicker';
import { exportInventoryCountSheet, parseInventoryCountSheet } from './inventoryExport';

const copy = PHARMACY_COPY.INVENTORY;

function varianceClass(variance) {
  if (variance == null) return '';
  if (variance > 0) return 'cp-qty cp-qty--in';
  if (variance < 0) return 'cp-qty cp-qty--out';
  return 'cp-qty cp-qty--match';
}

function formatVariance(variance) {
  if (variance == null) return '—';
  if (variance > 0) return `+${formatNumber(variance)}`;
  return formatNumber(variance);
}

function rowTone(invalid, uncounted, variance) {
  if (invalid) return 'cp-inv-row--invalid';
  if (uncounted) return 'cp-inv-row--uncounted';
  if (variance) return 'cp-inv-row--var';
  return 'cp-inv-row--ok';
}

const EMPTY_FOUND = { medication_id: '', lot_number: '', expiry_date: '', counted_qty: '' };

export default function PharmacyInventoryDetailPage() {
  const { inventoryId } = useParams();
  const navigate = useNavigate();
  const routes = useInventoryRoutes();
  const { toast } = usePharmacy();
  const fileRef = useRef(null);
  const lotRef = useRef(null);

  const [session, setSession] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [lineFilter, setLineFilter] = useState('all');
  const [foundOpen, setFoundOpen] = useState(false);
  const [found, setFound] = useState(EMPTY_FOUND);
  const [medications, setMedications] = useState([]);
  const [confirmClose, setConfirmClose] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim().toLowerCase()), 200);
    return () => clearTimeout(t);
  }, [searchInput]);

  const applySession = useCallback((data, { keepDirty = false } = {}) => {
    setSession(data);
    setDrafts((prev) => {
      const seeded = seedCountDrafts(data.lines || []);
      return keepDirty ? mergeKeptDrafts(data.lines || [], seeded, prev) : seeded;
    });
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await pharmacyService.getInventory(inventoryId);
      if (res.success && res.data) applySession(res.data);
      else {
        setSession(null);
        toast('danger', res.message || copy.NOT_FOUND);
      }
    } catch (e) {
      setSession(null);
      toast('danger', e.message || copy.NOT_FOUND);
    } finally {
      setLoading(false);
    }
  }, [inventoryId, toast, applySession]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    pharmacyService.listMedications({ status: 'ACTIVE' }).then((r) => {
      setMedications(r.success ? r.data || [] : []);
    });
  }, []);

  const counting = session?.status === 'COUNTING';
  const lines = useMemo(() => session?.lines || [], [session]);

  const linesWithMedContext = useMemo(() => {
    const totals = new Map();
    const counts = new Map();
    lines.forEach((line) => {
      const id = Number(line.medication_id);
      totals.set(id, (totals.get(id) || 0) + Number(line.theoretical_qty || 0));
      counts.set(id, (counts.get(id) || 0) + 1);
    });
    const seen = new Map();
    return lines.map((line) => {
      const id = Number(line.medication_id);
      const indexInMed = (seen.get(id) || 0) + 1;
      seen.set(id, indexInMed);
      const lotCount = counts.get(id) || 1;
      return {
        ...line,
        med_theo_total: totals.get(id) || 0,
        med_lot_count: lotCount,
        med_lot_index: indexInMed,
        show_med_total: lotCount > 1 && indexInMed === 1
      };
    });
  }, [lines]);

  const dirtyLines = useMemo(
    () => lines.filter((line) => isLineDirty(line, drafts[line.id])),
    [lines, drafts]
  );
  const invalidLines = useMemo(
    () => lines.filter((line) => Number.isNaN(parseCountedDraft(drafts[line.id]))),
    [lines, drafts]
  );

  const liveSummary = useMemo(() => {
    let counted = 0;
    let variances = 0;
    let surplus = 0;
    let shortage = 0;
    lines.forEach((line) => {
      const parsed = parseCountedDraft(drafts[line.id]);
      if (parsed === null || Number.isNaN(parsed)) return;
      counted += 1;
      const v = lineVariance(line.theoretical_qty, parsed);
      if (v > 0) {
        variances += 1;
        surplus += 1;
      } else if (v < 0) {
        variances += 1;
        shortage += 1;
      }
    });
    return {
      lines: lines.length,
      counted,
      uncounted: lines.length - counted,
      variances,
      surplus,
      shortage
    };
  }, [lines, drafts]);

  const visibleLines = useMemo(() => {
    return linesWithMedContext.filter((line) => {
      const parsed = parseCountedDraft(drafts[line.id]);
      if (lineFilter === 'uncounted' && parsed !== null && !Number.isNaN(parsed)) return false;
      if (lineFilter === 'variance') {
        const v = lineVariance(line.theoretical_qty, parsed);
        if (v == null || v === 0) return false;
      }
      if (!search) return true;
      const hay = `${line.medication_name || ''} ${line.lot_number || ''}`.toLowerCase();
      return hay.includes(search);
    });
  }, [linesWithMedContext, drafts, lineFilter, search]);

  const goBack = () => navigate(routes.LIST);

  const setDraft = (lineId, value) => {
    setDrafts((prev) => ({ ...prev, [lineId]: value }));
  };

  const payloadFromLines = (subset) => ({
    lines: subset.map((line) => ({
      id: line.id,
      counted_qty: parseCountedDraft(drafts[line.id])
    }))
  });

  const saveLines = async (subset = dirtyLines, { silent, keepDirty } = {}) => {
    if (!counting || !subset.length) return session;
    if (subset.some((line) => Number.isNaN(parseCountedDraft(drafts[line.id])))) {
      toast('warning', copy.INVALID_QTY);
      return null;
    }
    setBusy(true);
    try {
      const res = await pharmacyService.saveInventoryLines(session.id, payloadFromLines(subset));
      if (res.success && res.data) {
        applySession(res.data, { keepDirty: Boolean(keepDirty) });
        if (!silent) toast('success', copy.SAVE_OK);
        return res.data;
      }
      toast('danger', res.message || copy.FAIL);
      return null;
    } catch (e) {
      toast('danger', e.message || copy.FAIL);
      return null;
    } finally {
      setBusy(false);
    }
  };

  const onBlurLine = async (line) => {
    if (!counting || busy) return;
    if (!isLineDirty(line, drafts[line.id])) return;
    await saveLines([line], { silent: true, keepDirty: true });
  };

  const onCountKeyDown = (e, line) => {
    if (e.key === 'Enter' || e.key === 'ArrowDown') {
      e.preventDefault();
      focusAdjacentCount(line.id, 1);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      focusAdjacentCount(line.id, -1);
    }
  };

  const matchTheoretical = (line) => {
    setDraft(line.id, String(Number(line.theoretical_qty || 0)));
    requestAnimationFrame(() => focusAdjacentCount(line.id, 1));
  };

  const onExport = async () => {
    if (!session) return;
    try {
      let current = session;
      if (counting && dirtyLines.length) {
        current = await saveLines(dirtyLines, { silent: true });
        if (!current) return;
      }
      await exportInventoryCountSheet(current);
    } catch (e) {
      toast('danger', e.message || copy.FAIL);
    }
  };

  const onImportFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !counting) return;
    setBusy(true);
    try {
      const imported = await parseInventoryCountSheet(file);
      if (!imported.length) {
        toast('warning', copy.IMPORT_EMPTY);
        return;
      }
      const res = await pharmacyService.saveInventoryLines(session.id, { lines: imported });
      if (res.success && res.data) {
        applySession(res.data);
        toast('success', copy.IMPORT_OK.replace('{n}', formatNumber(imported.length)));
      } else {
        toast('danger', res.message || copy.FAIL);
      }
    } catch (err) {
      toast('danger', err.message || copy.FAIL);
    } finally {
      setBusy(false);
    }
  };

  const submitFoundLot = async () => {
    if (!found.medication_id || found.counted_qty === '') {
      toast('warning', copy.INVALID_QTY);
      return;
    }
    const qty = parseCountedDraft(found.counted_qty);
    if (qty === null || Number.isNaN(qty)) {
      toast('warning', copy.INVALID_QTY);
      return;
    }
    setBusy(true);
    try {
      const res = await pharmacyService.addInventoryLine(session.id, {
        medication_id: Number(found.medication_id),
        lot_number: found.lot_number,
        expiry_date: found.expiry_date || null,
        counted_qty: qty
      });
      if (res.success && res.data) {
        applySession(res.data, { keepDirty: true });
        setFoundOpen(false);
        setFound(EMPTY_FOUND);
        toast('success', copy.ADD_LOT_OK);
      } else {
        toast('danger', res.message || copy.FAIL);
      }
    } catch (e) {
      toast('danger', e.message || copy.FAIL);
    } finally {
      setBusy(false);
    }
  };

  const runClose = async () => {
    if (!session || !counting) return;
    if (invalidLines.length) {
      toast('warning', copy.INVALID_QTY);
      return;
    }
    if (dirtyLines.length) {
      const saved = await saveLines(dirtyLines, { silent: true });
      if (!saved) return;
    }
    setBusy(true);
    try {
      const res = await pharmacyService.closeInventory(session.id);
      if (res.success && res.data) {
        applySession(res.data);
        setConfirmClose(false);
        toast('success', copy.CLOSE_OK);
      } else {
        toast('danger', res.message || copy.FAIL);
      }
    } catch (e) {
      toast('danger', e.message || copy.FAIL);
    } finally {
      setBusy(false);
    }
  };

  const runCancel = async () => {
    if (!session || !counting) return;
    setBusy(true);
    try {
      const res = await pharmacyService.cancelInventory(session.id);
      if (res.success && res.data) {
        applySession(res.data);
        setConfirmCancel(false);
        toast('success', copy.CANCEL_OK);
      } else {
        toast('danger', res.message || copy.FAIL);
      }
    } catch (e) {
      toast('danger', e.message || copy.FAIL);
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <LoadingBlock label={copy.LOADING} />;
  if (!session) return <EmptyBlock label={copy.NOT_FOUND} />;

  const toggleFilter = (id) => setLineFilter((prev) => (prev === id ? 'all' : id));

  return (
    <div className={`cp-inv-page${counting ? ' cp-inv-page--counting' : ''}`}>
      <PharmacyPageHeader
        badge={copy.DETAIL_BADGE}
        title={session.session_number}
        description={
          <span className="d-inline-flex flex-wrap align-items-center gap-2">
            <span className={`cp-status-pill ${inventoryStatusClass(session.status)}`}>
              {statusLabel(session.status)}
            </span>
            <span className="text-muted">
              {inventorySessionDisplayName(session, copy.LOCATION_DUTY)}
            </span>
          </span>
        }
        actions={
          <button type="button" className="cp-btn cp-btn--ghost" onClick={goBack}>
            <Icon name="chevronLeft" size={16} aria-hidden />
            {copy.BACK}
          </button>
        }
      />

      <InventoryFlow status={session.status} />

      <div className="cp-order-meta cp-inv-session-meta">
        <div>
          <span className="cp-order-meta__label">{copy.META_SITE}</span>
          <strong>{inventorySessionDisplayName(session, copy.LOCATION_DUTY)}</strong>
        </div>
        <div>
          <span className="cp-order-meta__label">{copy.META_STARTED}</span>
          <strong>{formatDateTime(session.started_at)}</strong>
        </div>
        {session.closed_at ? (
          <div>
            <span className="cp-order-meta__label">{copy.META_CLOSED}</span>
            <strong>{formatDateTime(session.closed_at)}</strong>
          </div>
        ) : (
          <div>
            <span className="cp-order-meta__label">{copy.META_AUTHOR}</span>
            <strong>{session.created_by_name || '—'}</strong>
          </div>
        )}
      </div>
      {session.notes ? (
        <div className="cp-order-notes">
          <span className="cp-order-meta__label">{copy.META_NOTES}</span>
          <p className="mb-0">{session.notes}</p>
        </div>
      ) : null}

      <section className="cp-inv-hero" aria-label="Synthèse comptage">
        <InventoryProgress counted={liveSummary.counted} total={liveSummary.lines} />
        <div className="cp-inv-kpis">
          <button
            type="button"
            className={`cp-inv-kpi${lineFilter === 'all' ? ' is-active' : ''}`}
            onClick={() => setLineFilter('all')}
          >
            <span>{copy.STAT_LINES}</span>
            <strong>{formatNumber(liveSummary.lines)}</strong>
          </button>
          <div className="cp-inv-kpi cp-inv-kpi--ok">
            <span>{copy.STAT_COUNTED}</span>
            <strong>{formatNumber(liveSummary.counted)}</strong>
          </div>
          <button
            type="button"
            className={`cp-inv-kpi cp-inv-kpi--muted${lineFilter === 'uncounted' ? ' is-active' : ''}`}
            onClick={() => toggleFilter('uncounted')}
          >
            <span>{copy.STAT_UNCOUNTED}</span>
            <strong>{formatNumber(liveSummary.uncounted)}</strong>
          </button>
          <button
            type="button"
            className={`cp-inv-kpi cp-inv-kpi--warn${lineFilter === 'variance' ? ' is-active' : ''}`}
            onClick={() => toggleFilter('variance')}
          >
            <span>{copy.STAT_VAR}</span>
            <strong>{formatNumber(liveSummary.variances)}</strong>
            <em>
              +{formatNumber(liveSummary.surplus)} / {formatNumber(liveSummary.shortage)}
            </em>
          </button>
        </div>
      </section>

      <div className={`cp-inv-toolbar${counting ? ' is-sticky' : ''}`}>
        <div className="cp-search-wrap">
          <Icon name="search" size={16} className="cp-search-wrap__icon" aria-hidden />
          <input
            type="search"
            className="cp-search-wrap__input"
            placeholder={copy.SEARCH_LINES}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            aria-label={copy.SEARCH_LINES_ARIA}
          />
        </div>
        <div className="cp-filter-pills" role="group" aria-label={copy.FILTER_LINES_ARIA}>
          <button
            type="button"
            className={`cp-filter-pill${lineFilter === 'all' ? ' is-active' : ''}`}
            onClick={() => setLineFilter('all')}
          >
            {copy.FILTER_LINES_ALL}
            <span className="cp-filter-pill__count">{formatNumber(liveSummary.lines)}</span>
          </button>
          <button
            type="button"
            className={`cp-filter-pill${lineFilter === 'uncounted' ? ' is-active' : ''}`}
            onClick={() => toggleFilter('uncounted')}
          >
            {copy.FILTER_UNCOUNTED}
            <span className="cp-filter-pill__count">{formatNumber(liveSummary.uncounted)}</span>
          </button>
          <button
            type="button"
            className={`cp-filter-pill${lineFilter === 'variance' ? ' is-active' : ''}`}
            onClick={() => toggleFilter('variance')}
          >
            {copy.FILTER_VARIANCE}
            <span className="cp-filter-pill__count">{formatNumber(liveSummary.variances)}</span>
          </button>
        </div>
        <div className="cp-inv-toolbar__actions">
          {counting && dirtyLines.length ? <span className="cp-inv-dirty">{copy.DIRTY}</span> : null}
          <button type="button" className="cp-btn cp-btn--ghost cp-btn--sm" onClick={onExport} disabled={busy}>
            <Icon name="fileExcel" size={14} aria-hidden />
            {copy.EXPORT}
          </button>
          {counting ? (
            <>
              <button
                type="button"
                className="cp-btn cp-btn--ghost cp-btn--sm"
                disabled={busy}
                onClick={() => fileRef.current?.click()}
              >
                <Icon name="cloudUpload" size={14} aria-hidden />
                {copy.IMPORT}
              </button>
              <button
                type="button"
                className="cp-btn cp-btn--ghost cp-btn--sm"
                disabled={busy}
                onClick={() => {
                  setFound(EMPTY_FOUND);
                  setFoundOpen(true);
                }}
              >
                <Icon name="plus" size={14} aria-hidden />
                {copy.ADD_LOT}
              </button>
              <button
                type="button"
                className="cp-btn cp-btn--ghost cp-btn--sm"
                disabled={busy || !dirtyLines.length || invalidLines.length > 0}
                onClick={() => saveLines()}
              >
                {copy.SAVE}
                {dirtyLines.length ? ` (${dirtyLines.length})` : ''}
              </button>
              <button
                type="button"
                className="cp-btn cp-btn--primary cp-btn--sm"
                disabled={busy}
                onClick={() => setConfirmClose(true)}
              >
                {copy.CLOSE}
              </button>
              <button
                type="button"
                className="cp-btn cp-btn--ghost cp-btn--sm"
                disabled={busy}
                onClick={() => setConfirmCancel(true)}
              >
                {copy.CANCEL}
              </button>
            </>
          ) : null}
        </div>
      </div>

      <input ref={fileRef} type="file" accept=".xlsx" hidden onChange={onImportFile} />

      <div className="cp-card">
        <div className="cp-card__body cp-card__body--flush">
          {visibleLines.length === 0 ? (
            <EmptyBlock label={lines.length === 0 ? copy.EMPTY_LINES : copy.EMPTY_FILTER} />
          ) : (
            <div className="cp-table-wrap">
              <Table responsive className="cp-table cp-inv-table mb-0">
                <thead>
                  <tr>
                    <th>{copy.COL_MED}</th>
                    <th>{copy.COL_LOT}</th>
                    <th>{copy.COL_EXPIRY}</th>
                    <th className="text-end">{copy.COL_THEO}</th>
                    <th className="text-end">{copy.COL_COUNTED}</th>
                    <th className="text-end">{copy.COL_VAR}</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleLines.map((line) => {
                    const draft = drafts[line.id] ?? '';
                    const parsed = parseCountedDraft(draft);
                    const invalid = Number.isNaN(parsed);
                    const variance = lineVariance(line.theoretical_qty, parsed);
                    const uncounted = parsed === null;
                    return (
                      <tr key={line.id} className={rowTone(invalid, uncounted, variance)}>
                        <td>
                          <div className="cp-med-cell">
                            <span className="cp-med-cell__body">
                              <span className="cp-med-cell__name">{line.medication_name}</span>
                              <span className="cp-med-cell__desc">
                                {line.unit_name || copy.COL_UNIT}
                                {line.med_lot_count > 1
                                  ? ` · ${copy.LOT_OF_HINT.replace('{n}', String(line.med_lot_index)).replace(
                                      '{total}',
                                      String(line.med_lot_count)
                                    )}`
                                  : ''}
                                {line.is_new_lot ? (
                                  <span className="cp-inv-found-tag">{copy.NEW_LOT_TAG}</span>
                                ) : null}
                              </span>
                              {line.show_med_total ? (
                                <span className="cp-med-cell__desc text-muted">
                                  {copy.MED_TOTAL_HINT.replace('{lots}', formatNumber(line.med_lot_count)).replace(
                                    '{qty}',
                                    formatNumber(line.med_theo_total)
                                  )}
                                </span>
                              ) : null}
                            </span>
                          </div>
                        </td>
                        <td className="cp-num">{line.lot_number || '—'}</td>
                        <td>{line.expiry_date ? formatDate(line.expiry_date) : '—'}</td>
                        <td className="text-end cp-num">{formatNumber(line.theoretical_qty)}</td>
                        <td className="cp-inv-count-cell">
                          {counting ? (
                            <div className="cp-inv-count-wrap">
                              <Form.Control
                                size="sm"
                                type="text"
                                inputMode="numeric"
                                data-inv-count={line.id}
                                className={`cp-inv-count${invalid ? ' is-invalid' : ''}${
                                  !uncounted && !invalid && variance === 0 ? ' is-match' : ''
                                }`}
                                value={draft}
                                placeholder="—"
                                aria-label={`${copy.COL_COUNTED} ${line.medication_name}`}
                                onChange={(e) => setDraft(line.id, e.target.value)}
                                onBlur={() => onBlurLine(line)}
                                onKeyDown={(e) => onCountKeyDown(e, line)}
                                disabled={busy}
                              />
                              <button
                                type="button"
                                className="cp-inv-match"
                                title={copy.MATCH_THEO}
                                aria-label={copy.MATCH_ARIA.replace('{name}', line.medication_name || '')}
                                disabled={busy}
                                onClick={() => matchTheoretical(line)}
                              >
                                <Icon name="check" size={13} aria-hidden />
                              </button>
                            </div>
                          ) : parsed === null ? (
                            <span className="text-muted">{copy.UNCOUNTED}</span>
                          ) : (
                            <strong className="cp-num">{formatNumber(parsed)}</strong>
                          )}
                        </td>
                        <td className={`text-end ${varianceClass(variance)}`}>
                          {uncounted || invalid ? (
                            <span className="text-muted">—</span>
                          ) : variance === 0 ? (
                            <span className="cp-inv-match-label">
                              <Icon name="check" size={12} aria-hidden /> 0
                            </span>
                          ) : (
                            formatVariance(variance)
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          )}
        </div>
        {counting ? <p className="cp-inv-kbd-hint">{copy.KEYBOARD_HINT}</p> : null}
      </div>

      {counting ? (
        <div className="cp-inv-dock">
          <InventoryProgress counted={liveSummary.counted} total={liveSummary.lines} compact />
          <div className="cp-inv-dock__actions">
            <button type="button" className="cp-btn cp-btn--ghost cp-btn--sm" disabled={busy} onClick={() => setConfirmCancel(true)}>
              {copy.CANCEL}
            </button>
            <button
              type="button"
              className="cp-btn cp-btn--ghost"
              disabled={busy || !dirtyLines.length || invalidLines.length > 0}
              onClick={() => saveLines()}
            >
              {copy.SAVE}
              {dirtyLines.length ? ` (${dirtyLines.length})` : ''}
            </button>
            <button type="button" className="cp-btn cp-btn--primary" disabled={busy} onClick={() => setConfirmClose(true)}>
              {copy.CLOSE}
            </button>
          </div>
        </div>
      ) : null}

      <Modal
        show={foundOpen}
        onHide={() => !busy && setFoundOpen(false)}
        centered
        className="cp-modal cp-modal--inv"
        dialogClassName="cp-inv-modal-dialog"
      >
        <Modal.Header closeButton={!busy} className="cp-modal__header">
          <div className="cp-modal__intro">
            <span className="cp-modal__badge" aria-hidden>
              <Icon name="plus" size={18} />
            </span>
            <div>
              <span className="cp-modal__eyebrow">{copy.NEW_LOT_TAG}</span>
              <Modal.Title as="h2" className="cp-modal__title">
                {copy.ADD_LOT_TITLE}
              </Modal.Title>
              <p className="cp-modal__desc">{copy.ADD_LOT_DESC}</p>
            </div>
          </div>
        </Modal.Header>
        <Modal.Body className="cp-modal__body">
          <div className="cp-inv-modal-panel">
            {foundOpen ? (
              <MedicationSmartPicker
                medications={medications}
                value={found.medication_id}
                onChange={(id) => setFound((p) => ({ ...p, medication_id: id }))}
                onPicked={() => lotRef.current?.focus()}
                autoFocus
                disabled={busy}
              />
            ) : null}
            <div className="cp-inv-modal-grid">
              <Form.Group>
                <Form.Label className="cp-inv-modal-label">{copy.FIELD_LOT}</Form.Label>
                <Form.Control
                  ref={lotRef}
                  value={found.lot_number}
                  onChange={(e) => setFound((p) => ({ ...p, lot_number: e.target.value }))}
                />
              </Form.Group>
              <Form.Group>
                <Form.Label className="cp-inv-modal-label">{copy.FIELD_EXPIRY}</Form.Label>
                <Form.Control
                  type="date"
                  value={found.expiry_date}
                  onChange={(e) => setFound((p) => ({ ...p, expiry_date: e.target.value }))}
                />
              </Form.Group>
            </div>
            <Form.Group className="cp-inv-modal-qty">
              <Form.Label className="cp-inv-modal-label">{copy.FIELD_QTY}</Form.Label>
              <Form.Control
                type="number"
                min={0}
                step={1}
                value={found.counted_qty}
                onChange={(e) => setFound((p) => ({ ...p, counted_qty: e.target.value }))}
              />
              <p className="cp-inv-modal-hint">{copy.FIELD_QTY_HINT}</p>
            </Form.Group>
          </div>
        </Modal.Body>
        <Modal.Footer className="cp-modal__footer">
          <button type="button" className="cp-modal-btn cp-modal-btn--ghost" onClick={() => setFoundOpen(false)} disabled={busy}>
            {copy.CREATE_CANCEL}
          </button>
          <button
            type="button"
            className="cp-modal-btn cp-modal-btn--primary"
            disabled={busy || !found.medication_id || found.counted_qty === ''}
            onClick={submitFoundLot}
          >
            <Icon name="plus" size={16} aria-hidden />
            {copy.ADD_LOT_SUBMIT}
          </button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={confirmClose}
        onHide={() => !busy && setConfirmClose(false)}
        centered
        className="cp-modal cp-modal--inv cp-modal--inv-ok"
        dialogClassName="cp-inv-modal-dialog"
      >
        <Modal.Header closeButton={!busy} className="cp-modal__header">
          <div className="cp-modal__intro">
            <span className="cp-modal__badge" aria-hidden>
              <Icon name="check" size={18} />
            </span>
            <div>
              <span className="cp-modal__eyebrow">{copy.FLOW_CLOSE}</span>
              <Modal.Title as="h2" className="cp-modal__title">
                {copy.CLOSE_TITLE}
              </Modal.Title>
              <p className="cp-modal__desc">{copy.CLOSE_LEAD}</p>
            </div>
          </div>
        </Modal.Header>
        <Modal.Body className="cp-modal__body">
          <div className="cp-inv-modal-session">
            <span className="cp-inv-modal-session__num">{session.session_number}</span>
            <span className="cp-inv-modal-session__site">
              {inventorySessionDisplayName(session, copy.LOCATION_DUTY)}
            </span>
          </div>
          <InventoryProgress counted={liveSummary.counted} total={liveSummary.lines} />
          <div className="cp-inv-kpis cp-inv-kpis--modal">
            <div className="cp-inv-kpi cp-inv-kpi--ok">
              <span>{copy.STAT_COUNTED}</span>
              <strong>{formatNumber(liveSummary.counted)}</strong>
            </div>
            <div className="cp-inv-kpi cp-inv-kpi--muted">
              <span>{copy.STAT_UNCOUNTED}</span>
              <strong>{formatNumber(liveSummary.uncounted)}</strong>
            </div>
            <div className="cp-inv-kpi cp-inv-kpi--warn">
              <span>{copy.STAT_VAR}</span>
              <strong>{formatNumber(liveSummary.variances)}</strong>
            </div>
          </div>
          {liveSummary.uncounted > 0 ? (
            <div className="cp-inv-modal-callout">{copy.CLOSE_UNCOUNTED_HINT}</div>
          ) : (
            <div className="cp-inv-modal-callout cp-inv-modal-callout--ok">
              {copy.CLOSE_READY}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="cp-modal__footer">
          <button type="button" className="cp-modal-btn cp-modal-btn--ghost" onClick={() => setConfirmClose(false)} disabled={busy}>
            {copy.CREATE_CANCEL}
          </button>
          <button type="button" className="cp-modal-btn cp-modal-btn--primary" disabled={busy} onClick={runClose}>
            <Icon name="check" size={16} aria-hidden />
            {copy.CLOSE_APPLY}
          </button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={confirmCancel}
        onHide={() => !busy && setConfirmCancel(false)}
        centered
        className="cp-modal cp-modal--inv cp-modal--inv-danger"
        dialogClassName="cp-inv-modal-dialog"
      >
        <Modal.Header closeButton={!busy} className="cp-modal__header">
          <div className="cp-modal__intro">
            <span className="cp-modal__badge" aria-hidden>
              <Icon name="times" size={18} />
            </span>
            <div>
              <span className="cp-modal__eyebrow">{statusLabel('CANCELLED')}</span>
              <Modal.Title as="h2" className="cp-modal__title">
                {copy.CANCEL_TITLE}
              </Modal.Title>
              <p className="cp-modal__desc">{copy.CANCEL_LEAD}</p>
            </div>
          </div>
        </Modal.Header>
        <Modal.Body className="cp-modal__body">
          <div className="cp-inv-modal-session">
            <span className="cp-inv-modal-session__num">{session.session_number}</span>
            <span className="cp-inv-modal-session__site">
              {inventorySessionDisplayName(session, copy.LOCATION_DUTY)}
            </span>
          </div>
          <div className="cp-inv-modal-callout cp-inv-modal-callout--danger">{copy.CANCEL_LEAD}</div>
        </Modal.Body>
        <Modal.Footer className="cp-modal__footer">
          <button type="button" className="cp-modal-btn cp-modal-btn--ghost" onClick={() => setConfirmCancel(false)} disabled={busy}>
            {copy.CREATE_CANCEL}
          </button>
          <button type="button" className="cp-modal-btn cp-modal-btn--danger" disabled={busy} onClick={runCancel}>
            <Icon name="times" size={16} aria-hidden />
            {copy.CANCEL_APPLY}
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
